import { PfInput } from '@lidea/shared/ui/form';
import { Text } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { MeasureFormValues } from '../measure-form/measure-form';
import { PictureInput } from '../measure-form/picture-input';
import styles from './measures-inputs-list.module.css';

type Field = {
  label?: {
    fr: string;
    en: string;
  };
  name: string;
  value: number | string | null;
  image_url?: string | null;
  image_processing?: null | boolean;
  image_processing_error?: null | boolean;
  recommended_fec?: number | null;
};
/* eslint-disable-next-line */
export interface MeasuresInputsListProps {
  labelPlacement: 'stacked' | 'floating';
  form: UseFormReturnType<MeasureFormValues>;
  fieldsLabel?: string;
  fields: Field[];
  readonly?: boolean;
  plotId: string;
  analysisId?: number;
  measureId: number;
  measureZone: number;
  scrollOnFocus?: boolean;
}

function formatRecommendedFec(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export function MeasuresInputsList(props: MeasuresInputsListProps) {
  const { t, i18n } = useTranslation(['analysis'], { useSuspense: false });
  const label = (field: Field, index: number) => {
    if (field.label) {
      return field.label[i18n.language as 'en' | 'fr'];
    } else if (props.fieldsLabel) {
      return `${props.fieldsLabel}`;
    } else {
      return t('analysis:field_label');
    }
  };
  return (
    <div
      className={classNames(styles['container'], {
        [styles['no-gap']]: props.labelPlacement === 'stacked',
      })}
    >
      {props.fields.map((field, index) => {
        const fieldLabel = label(field, index);
        const hasRecommendedFec =
          field.recommended_fec != null && !Number.isNaN(field.recommended_fec);

        return (
          <div key={index}>
            {hasRecommendedFec ? (
              <div className={styles['field-label-row']}>
                <span className={styles['field-label']}>{fieldLabel}</span>
                <Text
                  className={styles['recommended-fec']}
                  component="span"
                  size="sm"
                >
                  {t('analysis:recommended_fec', {
                    value: formatRecommendedFec(field.recommended_fec!),
                  })}
                </Text>
              </div>
            ) : null}
            <PfInput
              key={index}
              ariaLabel={fieldLabel}
              label={hasRecommendedFec ? undefined : fieldLabel}
              readOnly={props.readonly}
              scrollOnFocus={props.scrollOnFocus}
              type="number"
              {...props.form.getInputProps(field.name)}
            />
            {typeof field.image_processing === 'boolean' && !props.readonly ? (
              <PictureInput
                analysisId={props.analysisId}
                fieldId={props.plotId}
                group={field.name}
                hasProcessError={field.image_processing_error}
                imageUrl={field.image_url ?? ''}
                isProcessing={field.image_processing === true}
                measureId={props.measureId}
                measureZone={props.measureZone}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export default MeasuresInputsList;
